import React from 'react';
import { Box, GlobalStyles } from '@mui/material';
import InterRegular from '../../../../fonts/Inter/Inter-Regular.woff2';
import InterMedium from '../../../../fonts/Inter/Inter-Medium.woff2';
import InterSemiBold from '../../../../fonts/Inter/Inter-SemiBold.woff2';
import InterBold from '../../../../fonts/Inter/Inter-Bold.woff2';
import InterExtraBold from '../../../../fonts/Inter/Inter-ExtraBold.woff2';
import bancameLogo from '../../../../assets/bancame_logo_dark.svg';
import cloudImage from '../../../../assets/plr_cloud.webp';
import buildingImage from '../../../../assets/plr_bg_building.webp';
import type { Partner } from '../../../../components/LeadFormIndex/types';
import {
  companyOverline, PLR_ALLIANCE_COPY, PLR_HERO_FALLBACK_IMAGE, PLR_RESULT_TIME, projectEntityLabel,
} from '../heroVariant';
import { plr, SHORT_DESKTOP } from './tokens';
import { clockIcon } from './icons';
import { PlrLayoutContext, readPlrVariant } from './layoutContext';
import type { PlrBackgroundTone } from './layoutContext';
import { PlrBackLink, ProjectRow } from './ui';

// Familia propia para tener los pesos reales del canvas (600 y 800) sin cambiar
// cómo se ven las demás pantallas que usan la familia "Inter" del theme.
const fontFaces = [
  [400, InterRegular],
  [500, InterMedium],
  [600, InterSemiBold],
  [700, InterBold],
  [800, InterExtraBold],
].map(([weight, src]) => (
  `@font-face{font-family:'Inter PLR';font-style:normal;font-weight:${weight};`
  + `font-display:swap;src:url(${src}) format('woff2');}`
)).join('');

// bancame_logo_dark.svg es 655x134: fijamos el ancho para evitar saltos al cargar.
const bancameLogoWidth = (height: number) => Math.round((height * 655) / 134);

export const isBancamePartner = (partnerName?: string) => partnerName?.trim().toLowerCase() === 'banca.me';

export const partnerDisplayName = (partner: Partner) => partner.partnerName?.trim() || 'la inmobiliaria';

// Logo del "powered by": el footerLogo del partner o, por defecto, el logo BNPL rectangular
// de banca.me (píldora morada, 1508.57 x 624).
export const PLR_DEFAULT_FOOTER_LOGO = 'https://assets.banca.me/bancame_logo_bnpl_rectangular.svg';
const defaultFooterLogoWidth = (height: number) => Math.round((height * 1508.57) / 624);

// Hay partners (p. ej. Brick) con el wordmark antiguo de banca.me como footerLogo: se cambia por
// el logo nuevo. Un footerLogo propio (de otra marca o ruta) se respeta tal cual.
const LEGACY_BANCAME_LOGO = /^https?:\/\/assets\.banca\.me\/bancame_logo(_dark|_white|_light)?\.svg$/i;
export const resolveFooterLogo = (footerLogo?: string) => {
  const url = footerLogo?.trim();
  if (!url || LEGACY_BANCAME_LOGO.test(url.split(/[?#]/)[0])) return undefined;
  return url;
};

export type PlrProgress = {
  step: 1 | 2 | 3,
  label: string,
  // 3f: el último segmento queda a medio llenar mientras calculamos.
  partial?: boolean,
};

export function PlrStepBar({ progress }: { progress: PlrProgress }) {
  const segments = [1, 2, 3].map((n) => {
    if (n < progress.step) return plr.stepDone;
    if (n > progress.step) return plr.stepTodo;
    if (progress.partial) return `linear-gradient(90deg, ${plr.stepCurrent} 55%, ${plr.stepTodo} 55%)`;
    return progress.step === 3 ? plr.stepDone : plr.stepCurrent;
  });
  return (
    <Box
      data-pw="preloan-step-bar"
      sx={{
        display: 'flex', flexDirection: 'column', gap: '8px', [SHORT_DESKTOP]: { gap: '6px' },
      }}
    >
      <Box component="span" sx={{ fontSize: 13, color: plr.muted }}>
        {`Paso ${progress.step} de 3 · ${progress.label}`}
      </Box>
      <Box
        role="progressbar"
        aria-label={`Paso ${progress.step} de 3`}
        aria-valuemin={1}
        aria-valuemax={3}
        aria-valuenow={progress.step}
        sx={{ display: 'flex', gap: '6px' }}
      >
        {segments.map((background, i) => (
          <Box
            // eslint-disable-next-line react/no-array-index-key
            key={i}
            component="span"
            sx={{
              flex: 1, height: 4, borderRadius: '2px', background,
            }}
          />
        ))}
      </Box>
    </Box>
  );
}

// Al crear un partner sin logo, el backend le pone el de banca.me como headerLogo
// (bancame_logo_dark.svg): no es su logo, y banca.me ya está en el "powered by".
export const isBancameDefaultHeaderLogo = (headerLogo?: string) => {
  const url = headerLogo?.trim();
  return !!url && LEGACY_BANCAME_LOGO.test(url.split(/[?#]/)[0]);
};

function PartnerLogo({ partner, desktop }: { partner: Partner, desktop?: boolean }) {
  const logo = partner.config.styles?.headerLogo;
  const height = desktop ? 30 : 24;
  if (isBancamePartner(partner.partnerName)) {
    const h = desktop ? 24 : 20;
    return <img src={bancameLogo} alt="banca.me" style={{ height: h, width: bancameLogoWidth(h) }} />;
  }
  if (isBancameDefaultHeaderLogo(logo)) return null;
  if (logo) {
    return (
      <img
        src={logo}
        alt={partnerDisplayName(partner)}
        style={{
          height, width: 'auto', maxWidth: desktop ? 220 : 170, objectFit: 'contain', display: 'block',
        }}
      />
    );
  }
  return (
    <Box component="span" sx={{ fontSize: desktop ? 17 : 15, fontWeight: 800, color: plr.ink }}>
      {partnerDisplayName(partner)}
    </Box>
  );
}

PartnerLogo.defaultProps = { desktop: false };

// footerLogo del partner → logo BNPL por defecto → logo local (si falla la red).
function PoweredByLogo({ partner, desktop }: { partner: Partner, desktop?: boolean }) {
  const custom = resolveFooterLogo(partner.config.styles?.footerLogo);
  const sources = React.useMemo(
    () => [custom, PLR_DEFAULT_FOOTER_LOGO, bancameLogo].filter((src): src is string => !!src),
    [custom],
  );
  const [index, setIndex] = React.useState(0);
  React.useEffect(() => setIndex(0), [sources]);
  const src = sources[Math.min(index, sources.length - 1)];
  let height = desktop ? 24 : 20;
  let width: number | 'auto' = 'auto';
  if (src === PLR_DEFAULT_FOOTER_LOGO) width = defaultFooterLogoWidth(height);
  if (src === bancameLogo) {
    height = desktop ? 17 : 14;
    width = bancameLogoWidth(height);
  }
  return (
    <img
      src={src}
      alt="banca.me"
      data-pw="preloan-powered-by-logo"
      onError={() => setIndex((i) => i + 1)}
      style={{
        height,
        width,
        maxWidth: desktop ? 120 : 96,
        objectFit: 'contain',
        display: 'block',
      }}
    />
  );
}

PoweredByLogo.defaultProps = { desktop: false };

// El header es idéntico en todos los pasos (sin botón de volver) para que la transición
// entre pantallas no mueva los logos.
function PlrHeader({ partner, desktop }: { partner: Partner, desktop?: boolean }) {
  return (
    <Box
      component="header"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        ...(desktop ? {} : { height: 52, flexShrink: 0, px: '20px' }),
      }}
    >
      <PartnerLogo partner={partner} desktop={desktop} />
      {!isBancamePartner(partner.partnerName) && (
        <Box
          component="span"
          sx={{
            ml: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: desktop ? '6px' : '5px',
            fontSize: desktop ? 12 : 10.5,
            color: plr.faint,
            whiteSpace: 'nowrap',
          }}
        >
          powered by
          <PoweredByLogo partner={partner} desktop={desktop} />
        </Box>
      )}
    </Box>
  );
}

PlrHeader.defaultProps = { desktop: false };

// "Volver" bajo la barra de pasos. Sin onBack se reserva el mismo alto (invisible) para que
// el contenido no salte entre pasos.
function BackLink({ onBack }: { onBack?: () => void }) {
  return (
    <Box sx={{ mt: '10px', height: 32, [SHORT_DESKTOP]: { mt: '6px' } }}>
      <PlrBackLink testId="preloan-cu-back" onClick={onBack} hidden={!onBack}>
        Volver
      </PlrBackLink>
    </Box>
  );
}

BackLink.defaultProps = { onBack: undefined };

// Card "glass" del panel: vidrio esmerilado con texto blanco. Un degradado oscuro en la
// parte baja del panel (scrim) asegura el contraste sobre fotos claras.
const glassCardSx = {
  position: 'absolute',
  left: 24,
  right: 24,
  bottom: 24,
  borderRadius: '22px',
  p: '20px 22px',
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
  color: '#FFFFFF',
  // Vidrio tintado con el morado de marca: mantiene el efecto glass y el contraste del
  // texto blanco (≥ 4.5:1) incluso sobre fotos claras.
  background: 'linear-gradient(135deg, rgba(255,255,255,0.14) 0%, rgba(41,29,94,0.30) 45%, rgba(26,20,61,0.42) 100%)',
  border: '1px solid rgba(255,255,255,0.38)',
  backdropFilter: 'blur(18px) saturate(140%)',
  WebkitBackdropFilter: 'blur(18px) saturate(140%)',
  boxShadow: '0 12px 32px rgba(26,20,61,0.18), inset 0 1px 0 rgba(255,255,255,0.35)',
  textShadow: '0 1px 2px rgba(26,20,61,0.25)',
} as const;

const chipSx = {
  alignSelf: 'flex-start',
  mt: '8px',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  background: 'rgba(255,255,255,0.16)',
  border: '1px solid rgba(255,255,255,0.32)',
  borderRadius: '18px',
  padding: '8px 13px',
  fontSize: 13,
  fontWeight: 600,
  color: '#FFFFFF',
  whiteSpace: 'nowrap',
} as const;

// Imagen del proyecto; si no hay o no carga, la imagen por defecto (PLR_HERO_FALLBACK_IMAGE:
// foto de una persona viendo proyectos en el celular). Las dos llevan el mismo tratamiento
// apagado para que la imagen acompañe y no compita con el formulario.
const useHeroImage = (partner: Partner) => {
  const url = partner.config.styles?.backgroundImage;
  const [failed, setFailed] = React.useState(false);
  React.useEffect(() => setFailed(false), [url]);
  const image = url && !failed ? url : PLR_HERO_FALLBACK_IMAGE;
  return { image, isDefault: image === PLR_HERO_FALLBACK_IMAGE, onError: () => setFailed(true) };
};

function ProjectPanel({ partner, showResultTime }: { partner: Partner, showResultTime: boolean }) {
  const { image, isDefault, onError } = useHeroImage(partner);
  const project = projectEntityLabel(partner.partnerEntityName, partner.partnerName);
  const company = companyOverline(partner.partnerEntityName, partner.partnerName);
  return (
    <Box
      component="aside"
      data-pw="preloan-project-card"
      sx={{
        display: { xs: 'none', lg: 'flex' },
        flex: '5 1 480px',
        minWidth: 0,
        position: 'sticky',
        top: 0,
        alignSelf: 'flex-start',
        // Panel compacto (máx. 620 px de alto) centrado verticalmente: deja respirar
        // la imagen en pantallas altas sin que domine la página.
        height: '100vh',
        minHeight: 560,
        alignItems: 'center',
        p: '24px 32px 24px 56px',
        boxSizing: 'border-box',
        overflow: 'hidden',
        // Fondo partido: morado oscuro de la paleta arriba y blanco abajo; la foto queda
        // encima del corte. Nubes decorativas sobre el morado.
        background: `linear-gradient(180deg, transparent 0%, transparent 56%, #FFFFFF 56%),
          linear-gradient(115deg, ${plr.ink} 0%, ${plr.hero} 55%, #3F2F8C 100%)`,
      }}
    >
      <Box
        component="img"
        src={cloudImage}
        alt=""
        aria-hidden="true"
        sx={{
          position: 'absolute', top: '3%', left: '-70px', width: 480, opacity: 0.9, pointerEvents: 'none',
        }}
      />
      <Box
        component="img"
        src={cloudImage}
        alt=""
        aria-hidden="true"
        sx={{
          position: 'absolute',
          top: '24%',
          right: '-90px',
          width: 340,
          opacity: 0.55,
          transform: 'scaleX(-1)',
          pointerEvents: 'none',
        }}
      />
      <Box sx={{
        flex: 1,
        maxWidth: 880,
        mx: 'auto',
        zIndex: 1,
        boxShadow: '0 24px 48px rgba(26,20,61,0.28)',
        height: 'min(620px, 100%)',
        position: 'relative',
        borderRadius: '24px',
        overflow: 'hidden',
        background: plr.panel,
      }}
      >
        <Box
          component="img"
          src={image}
          alt=""
          data-pw={isDefault ? 'preloan-hero-default' : undefined}
          onError={onError}
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            // Por defecto: el celular y las tarjetas flotantes quedan sobre la card glass.
            objectPosition: isDefault ? 'center 34%' : 'center 40%',
            display: 'block',
            filter: 'saturate(0.45)',
            // Desktop: opacidad completa (en mobile la banda sigue al 60 %).
          }}
        />
        <Box
          aria-hidden="true"
          sx={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            height: '62%',
            background: 'linear-gradient(180deg, rgba(26,20,61,0) 0%, rgba(26,20,61,0.38) 45%, rgba(26,20,61,0.62) 100%)',
          }}
        />
        <Box data-pw="preloan-project-glass" sx={glassCardSx}>
          {company && (
            <Box
              component="span"
              sx={{
                fontSize: 12.5,
                fontWeight: 700,
                color: 'rgba(255,255,255,0.88)',
                textTransform: 'uppercase',
                letterSpacing: '1px',
              }}
            >
              {company}
            </Box>
          )}
          <Box
            component="span"
            data-pw="preloan-project-name"
            sx={{
              fontSize: 30, lineHeight: 1.1, fontWeight: 800, letterSpacing: '-0.8px', color: '#FFFFFF',
            }}
          >
            {project}
          </Box>
          <Box component="span" sx={{ fontSize: 15, color: 'rgba(255,255,255,0.88)' }}>
            {PLR_ALLIANCE_COPY}
          </Box>
          {showResultTime && (
            <Box component="span" sx={chipSx}>
              {clockIcon({ size: 16 })}
              {PLR_RESULT_TIME}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

// Footer de todas las pantallas del flujo: términos (del partner, por ?origin=), privacidad
// y ayuda (Intercom). En desktop queda alineado al ancho del contenido.
// Footer mínimo: solo el copyright (los links legales están en 1f y 2g).
function PlrFooter() {
  return (
    <Box
      component="footer"
      data-pw="preloan-footer"
      sx={{
        borderTop: `1px solid ${plr.divider}`,
        mt: { xs: '8px', sm: '24px' },
        p: { xs: '16px 24px 20px', sm: '18px 0 0' },
        // Con poca altura se oculta: el logo de banca.me ya está en el header ("powered by").
        [SHORT_DESKTOP]: { display: 'none' },
        display: 'flex',
        justifyContent: 'center',
        fontSize: { xs: 12, sm: 12.5 },
        color: plr.muted,
      }}
    >
      <span>{`© ${new Date().getFullYear()} banca.me`}</span>
    </Box>
  );
}

type PlrShellProps = {
  partner: Partner,
  children: React.ReactNode,
  // Muestra foto, chip y panel del proyecto (apagado con ?hero=0, hideHero o banca.me).
  showHero: boolean,
  onBack?: () => void,
  progress?: PlrProgress,
  // Barra de pasos en mobile (en 1f se omite: hay banda de foto y "Paso 1 de 3" junto al RUT).
  progressOnMobile?: boolean,
  // Banda de foto en mobile (solo 1f).
  photoBand?: boolean,
  // Chip "Resultado en cerca de 3 minutos" en el panel (solo 1f).
  panelResultTime?: boolean,
  footer?: React.ReactNode,
  contentMaxWidth?: number,
  testId?: string,
};

// Gris banca.me (theme palette.gray.main / background.defaultSecondary).
const BANCAME_GRAY = '#F2F2F2';

// Variante centrada: fondo a pantalla completa con la foto de un edificio bajo un filtro de
// color (BuildingBackdrop). Este color queda de respaldo mientras carga la foto.
const centeredBackground = (tone: PlrBackgroundTone) => {
  if (tone === 'purple') return `linear-gradient(115deg, ${plr.accent} 0%, #5D47B8 55%, #755DDF 100%)`;
  if (tone === 'darkPurple') return `linear-gradient(115deg, ${plr.ink} 0%, ${plr.hero} 55%, #3F2F8C 100%)`;
  return BANCAME_GRAY;
};

// Nombre del proyecto sobre la foto (variante ?nombre=foto): mismo vidrio que el panel,
// más compacto, con un degradado oscuro abajo para el contraste del texto blanco.
function PhotoGlass({ partner, desktop }: { partner: Partner, desktop?: boolean }) {
  const project = projectEntityLabel(partner.partnerEntityName, partner.partnerName);
  const company = companyOverline(partner.partnerEntityName, partner.partnerName);
  return (
    <>
      <Box
        aria-hidden="true"
        sx={{
          position: 'absolute',
          inset: 'auto 0 0 0',
          height: '70%',
          background: 'linear-gradient(180deg, rgba(26,20,61,0) 0%, rgba(26,20,61,0.55) 100%)',
        }}
      />
      <Box
        data-pw="preloan-photo-glass"
        sx={{
          ...glassCardSx,
          left: desktop ? 16 : 12,
          right: desktop ? 16 : 12,
          bottom: desktop ? 16 : 12,
          borderRadius: desktop ? '16px' : '14px',
          p: desktop ? '14px 18px' : '10px 14px',
          gap: '2px',
        }}
      >
        {company && (
          <Box
            component="span"
            sx={{
              fontSize: desktop ? 11.5 : 10.5,
              fontWeight: 700,
              letterSpacing: '0.9px',
              textTransform: 'uppercase',
              color: 'rgba(255,255,255,0.88)',
            }}
          >
            {company}
          </Box>
        )}
        <Box
          component="span"
          data-pw="preloan-project-name"
          sx={{
            fontSize: desktop ? 22 : 18, lineHeight: 1.15, fontWeight: 800, letterSpacing: '-0.4px',
          }}
        >
          {project}
        </Box>
        {desktop && (
          <Box component="span" sx={{ fontSize: 13, color: 'rgba(255,255,255,0.88)' }}>
            {PLR_ALLIANCE_COPY}
          </Box>
        )}
      </Box>
    </>
  );
}

PhotoGlass.defaultProps = { desktop: false };
const centeredCardShadow = `0 0 0 1px ${plr.divider}, 0 24px 64px rgba(26,20,61,0.22)`;

// Alto mínimo de la card centrada (tablet y desktop): el mismo en todos los pasos, para que la
// card no cambie de tamaño al avanzar (p. ej. del RUT a la carga). Si el contenido es más alto,
// la card crece y la página hace scroll.
const CENTERED_CARD_MIN_HEIGHT = 'min(880px, calc(100vh - 48px))';
// Con poca altura, 12 px de margen arriba y abajo (ver SHORT_DESKTOP).
const SHORT_CARD_MIN_HEIGHT = 'calc(100vh - 24px)';
const headerGap = { mt: '20px', [SHORT_DESKTOP]: { mt: '12px' } };

// Filtro sobre la foto (en escala de grises): gris banca.me por defecto; morado claro u oscuro
// con ?fondo=morado | oscuro. Casi opaco, para que el edificio sea solo una textura.
const backdropFilter = (tone: PlrBackgroundTone) => {
  if (tone === 'purple') {
    return 'linear-gradient(115deg, rgba(74,56,160,0.86) 0%, rgba(93,71,184,0.82) 55%, rgba(117,93,223,0.78) 100%)';
  }
  if (tone === 'darkPurple') {
    return 'linear-gradient(115deg, rgba(41,29,94,0.9) 0%, rgba(51,38,117,0.86) 55%, rgba(63,47,140,0.82) 100%)';
  }
  return 'linear-gradient(180deg, rgba(242,242,242,0.82) 0%, rgba(242,242,242,0.9) 100%)';
};

// Fondo a pantalla completa (tablet y desktop): foto genérica de un edificio bajo el filtro,
// fija a la ventana mientras la card hace scroll encima. En mobile el fondo es blanco.
function BuildingBackdrop({ tone }: { tone: PlrBackgroundTone }) {
  return (
    <Box
      aria-hidden="true"
      data-pw="preloan-building-backdrop"
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 0,
        pointerEvents: 'none',
        display: { xs: 'none', sm: 'block' },
        backgroundImage: `${backdropFilter(tone)}, url(${buildingImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center 35%',
      }}
    />
  );
}

// Borde fino a ambos lados + sombra suave: delimita la columna del fondo tintado.
const sheetShadow = `0 0 0 1px ${plr.divider}, 0 0 32px rgba(41,29,94,0.06)`;

export default function PlrShell(props: PlrShellProps) {
  const {
    partner, children, showHero, onBack, progress, progressOnMobile = true,
    photoBand = false, panelResultTime = false, footer, contentMaxWidth = 520, testId,
  } = props;
  const { image, isDefault, onError } = useHeroImage(partner);
  // Variantes por URL (ver layoutContext): centrado, fondo claro, nombre sobre la foto.
  const [{
    centered, backgroundTone, nameOnPhoto, image: imageMode, thumbShape, thumbGlass,
  }] = React.useState(readPlrVariant);
  const withPanel = showHero && !centered;
  const bandVisible = photoBand && showHero && imageMode === 'full';
  const bandInColumn = centered && bandVisible;
  const glassMobile = nameOnPhoto && bandVisible;
  const glassDesktop = glassMobile && centered;
  // imagen=mini: logos, divider, fila del proyecto y barra de pasos en todos los pasos, para
  // que el header no cambie de forma entre pantallas.
  const projectHeader = showHero && imageMode === 'mini';
  const stepBarOnMobile = !!progress && (progressOnMobile || projectHeader);
  // Card centrada: el contenido parte arriba, pegado a la barra de pasos (el título no salta
  // entre pasos) y el "Volver" solo ocupa espacio en los pasos que lo tienen. En las otras
  // variantes se reserva su alto y el contenido va centrado en la hoja.
  const reserveBack = !!onBack || (!!progress && !bandInColumn && !centered);
  let contentTop = '28px';
  if (projectHeader && reserveBack) contentTop = centered ? '10px' : '12px';
  else if (centered) contentTop = '24px';
  // Algo menos de aire sobre el footer en la card, para que el paso de ClaveÚnica (el más
  // largo) quepa en el alto mínimo de una pantalla de 900 px.
  const contentBottom = centered ? '24px' : '32px';
  const columnWidth = contentMaxWidth;
  const content = children;
  const layout = React.useMemo(() => ({
    panel: withPanel,
    nameOnPhotoMobile: glassMobile,
    nameOnPhotoDesktop: glassDesktop,
    projectInHeader: projectHeader,
    stepBarOnMobile,
  }), [withPanel, glassMobile, glassDesktop, projectHeader, stepBarOnMobile]);
  const headerDivider = <Box sx={{ borderBottom: `1px solid ${plr.divider}` }} />;
  const projectRow = projectHeader && (
    <ProjectRow
      name={projectEntityLabel(partner.partnerEntityName, partner.partnerName)}
      company={companyOverline(partner.partnerEntityName, partner.partnerName)}
      thumb={image}
      shape={thumbShape}
      glass={thumbGlass}
    />
  );

  return (
    <PlrLayoutContext.Provider value={layout}>
      <Box
        data-pw={testId}
        sx={{
          minHeight: '100vh',
          width: '100%',
          display: 'flex',
          position: 'relative',
          overflowX: centered ? 'hidden' : undefined,
          // Mobile: todo blanco. Tablet (o desktop sin foto): la columna blanca es una hoja
          // sobre fondo tintado. Desktop con foto: el panel trae su propio fondo. Centrado:
          // morado con la foto del edificio a pantalla completa, y la columna como card encima.
          background: centered
            ? { xs: '#FFFFFF', sm: centeredBackground(backgroundTone) }
            : { xs: '#FFFFFF', sm: plr.page },
          color: plr.ink,
          fontFamily: plr.font,
          textAlign: 'left',
          '& a': { color: plr.ink },
          '& a:hover': { color: plr.inkHover },
        }}
      >
        <GlobalStyles styles={fontFaces} />
        {centered && <BuildingBackdrop tone={backgroundTone} />}
        {/* Mobile: todo blanco. Tablet y desktop: la columna del formulario es una hoja
          blanca a todo el alto (borde fino + sombra) centrada sobre el fondo tintado. */}
        <Box sx={{
          flex: '6 1 560px',
          minWidth: 0,
          display: 'flex',
          justifyContent: 'center',
          // Centrado: la card queda al medio del alto de la pantalla (mismo espacio arriba y
          // abajo); si es más alta que la pantalla, el contenedor crece y se hace scroll.
          ...(centered ? {
            alignItems: { sm: 'center' }, p: { sm: '24px' }, [SHORT_DESKTOP]: { py: '12px' },
          } : {}),
        }}
        >
          <Box
            component="main"
            sx={{
              flex: '1 1 auto',
              width: '100%',
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              boxSizing: 'border-box',
              minHeight: centered ? { xs: '100vh', sm: CENTERED_CARD_MIN_HEIGHT } : '100vh',
              position: 'relative',
              zIndex: 1,
              background: '#FFFFFF',
              boxShadow: centered ? { sm: centeredCardShadow } : { sm: sheetShadow },
              borderRadius: centered ? { sm: '24px' } : 0,
              p: centered
                ? { xs: 0, sm: '28px 48px 24px', md: '28px 64px 24px' }
                : { xs: 0, sm: '32px 48px', md: '32px 64px' },
              maxWidth: { sm: columnWidth + 128 },
              [SHORT_DESKTOP]: centered
                ? { minHeight: SHORT_CARD_MIN_HEIGHT, pt: '18px', pb: '14px' }
                : { pt: '20px', pb: '16px' },
            }}
          >
            <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
              <PlrHeader partner={partner} />
              {projectHeader && (
                <>
                  {headerDivider}
                  <Box sx={{ p: '16px 24px 0' }}>{projectRow}</Box>
                </>
              )}
            </Box>
            {/* Desktop: la barra de pasos va bajo los logos y hace de divider del header. */}
            <Box sx={{
              display: { xs: 'none', sm: 'block' }, width: '100%', maxWidth: columnWidth, mx: { lg: 'auto' },
            }}
            >
              <PlrHeader partner={partner} desktop />
              {projectHeader && (
                <>
                  <Box sx={headerGap}>{headerDivider}</Box>
                  <Box sx={headerGap}>{projectRow}</Box>
                </>
              )}
              <Box sx={headerGap}>
                {progress ? <PlrStepBar progress={progress} /> : headerDivider}
              </Box>
              {bandInColumn && (
              <Box sx={{
                mt: '20px',
                height: glassDesktop ? 220 : 200,
                position: 'relative',
                borderRadius: '18px',
                overflow: 'hidden',
                background: plr.panel,
              }}
              >
                <img
                  data-pw="preloan-hero-column"
                  src={image}
                  alt=""
                  onError={onError}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: isDefault ? 'center 34%' : 'center 45%',
                    display: 'block',
                  }}
                />
                {glassDesktop && <PhotoGlass partner={partner} desktop />}
              </Box>
              )}
              {/* Los resultados no tienen barra ni "Volver" (ver reserveBack). */}
              {reserveBack && <BackLink onBack={onBack} />}
            </Box>
            {bandVisible && (
            <Box sx={{
              display: { xs: 'block', sm: 'none' },
              position: 'relative',
              height: glassMobile ? 132 : 116,
              flexShrink: 0,
              overflow: 'hidden',
            }}
            >
              <img
                data-pw={isDefault ? 'preloan-hero-default' : 'preloan-hero'}
                src={image}
                alt=""
                onError={onError}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  // Por defecto: banda centrada en el celular y las tarjetas de proyectos.
                  objectPosition: isDefault ? 'center 36%' : 'center',
                  display: 'block',
                  // Con el nombre encima, la foto va completa y el degradado da el contraste.
                  opacity: glassMobile ? 1 : 0.6,
                }}
              />
              {glassMobile && <PhotoGlass partner={partner} />}
            </Box>
            )}
            {progress && stepBarOnMobile && (
            <Box sx={{ display: { xs: 'block', sm: 'none' }, p: projectHeader ? '16px 24px 0' : '2px 24px 0' }}>
              <PlrStepBar progress={progress} />
            </Box>
            )}
            {onBack && (
            <Box sx={{ display: { xs: 'block', sm: 'none' }, px: '24px' }}>
              <BackLink onBack={onBack} />
            </Box>
            )}
            <Box sx={{
              flex: 1,
              display: 'flex',
              alignItems: { xs: 'stretch', sm: centered ? 'stretch' : 'center' },
              justifyContent: { lg: 'center' },
            }}
            >
              <Box sx={{
                width: '100%',
                maxWidth: { sm: contentMaxWidth },
                minHeight: { xs: '100%', sm: 0 },
                display: 'flex',
                flexDirection: 'column',
                gap: { xs: '14px', sm: '16px' },
                // Con la fila del proyecto en el header, el "Volver" reservado ya da aire arriba.
                p: { xs: '20px 24px 16px', sm: `${contentTop} 0 ${contentBottom}` },
                [SHORT_DESKTOP]: {
                  gap: '12px',
                  pt: reserveBack && projectHeader ? '6px' : '16px',
                  pb: '12px',
                },
                boxSizing: 'border-box',
              }}
              >
                {content}
              </Box>
            </Box>
            {footer && (
            <Box sx={{ p: { xs: '0 24px 14px', sm: 0 } }}>
              {footer}
            </Box>
            )}
            <Box sx={{ width: '100%', maxWidth: { sm: columnWidth }, mx: { lg: 'auto' } }}>
              <PlrFooter />
            </Box>
          </Box>
        </Box>
        {withPanel && <ProjectPanel partner={partner} showResultTime={panelResultTime} />}
      </Box>
    </PlrLayoutContext.Provider>
  );
}

PlrShell.defaultProps = {
  onBack: undefined,
  progress: undefined,
  progressOnMobile: true,
  photoBand: false,
  panelResultTime: false,
  footer: undefined,
  contentMaxWidth: 520,
  testId: undefined,
};
